import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DeleteTypes, PaymentStatus } from '../../../services/api.types';
import { ConfirmationModal } from '../../ConfirmationModal';
import { Radio } from '../../Radio';
import { ClassEventConfirmationModal } from '../ClassEventConfirmationModal';

export interface UpdateClassDateModalValues {
  type: DeleteTypes;
  classTimeConflictConsent?: boolean;
  paymentClassConsent?: boolean;
  changeValueJustification?: string;
}

export interface UpdateClassDateModalProps {
  mode: 'delete' | 'update';
  availableOptions: DeleteTypes[];
  open: boolean;
  onClose: () => void;
  onChoose: (
    type: DeleteTypes,
    changeValueJustification?: string,
    gaveBypassConflictConsent?: boolean,
    gaveBypassPaymentClassConsent?: boolean
  ) => void;
  onDecline?: () => void;
  isLoading?: boolean;
  paymentStatusesInRange?: PaymentStatus[];
  needsClassTimeConflictConsent?: boolean;
  needsJustification?: boolean;
  lastJustification?: string;
}

export const UpdateClassDateModal = ({
  mode,
  availableOptions,
  open,
  onClose,
  onChoose,
  onDecline,
  isLoading,
  paymentStatusesInRange,
  needsClassTimeConflictConsent,
  needsJustification,
  lastJustification,
}: UpdateClassDateModalProps) => {
  const eventValues = useForm<UpdateClassDateModalValues>({
    mode: 'all',
    defaultValues: {
      type: DeleteTypes.DATE,
      classTimeConflictConsent: false,
      paymentClassConsent: false,
      changeValueJustification: lastJustification || '',
    },
  });

  const [openLastConfirmationModal, setOpenLastConfirmationModal] =
    useState<boolean>(false);

  const onSubmit = (data: UpdateClassDateModalValues) => {
    onChoose(
      data.type,
      data.changeValueJustification,
      data.classTimeConflictConsent,
      data.paymentClassConsent
    );
  };

  const options = useMemo(() => {
    const _options = [];
    if (availableOptions.includes(DeleteTypes.DATE)) {
      _options.push({
        label: 'Este evento',
        value: DeleteTypes.DATE,
      });
    }

    if (availableOptions.includes(DeleteTypes.UNTIL)) {
      _options.push({
        label: 'Este e os eventos seguintes',
        value: DeleteTypes.UNTIL,
      });
    }

    return _options;
  }, [availableOptions]);

  useEffect(() => {
    eventValues.setValue(
      'type',
      availableOptions.includes(DeleteTypes.DATE)
        ? DeleteTypes.DATE
        : DeleteTypes.UNTIL
    );
  }, [eventValues, availableOptions]);

  return (
    <div>
      <ConfirmationModal
        open={open}
        acceptText={mode === 'delete' ? 'Excluir' : 'Alterar'}
        declineText="Cancelar"
        onAccept={() => {
          onClose();
          setOpenLastConfirmationModal(true);
        }}
        onDecline={() => {
          onClose();
          onDecline?.();
        }}
        isLoading={isLoading}
      >
        <FormProvider {...eventValues}>
          <form className="w-full p-4">
            <div className="w-full">
              <Radio
                label={mode === 'delete' ? 'Excluir evento' : 'Alterar evento'}
                name="type"
                options={options}
              />
            </div>
          </form>
        </FormProvider>
      </ConfirmationModal>

      <ClassEventConfirmationModal
        mode={mode}
        open={openLastConfirmationModal}
        onAccept={async () => {
          eventValues.handleSubmit(onSubmit)();
        }}
        onClose={() => {
          setOpenLastConfirmationModal(false);
        }}
        paymentStatusesInRange={paymentStatusesInRange}
        needsClassTimeConflictConsent={
          mode === 'update' && needsClassTimeConflictConsent
        }
        needsJustification={mode === 'update' && needsJustification}
        formContext={eventValues}
      />
    </div>
  );
};
